/**
 * @file     anomalies.ts
 * @purpose  Detect data-quality + signal flags from the ScoringInput.
 *           Pure functions; runEngine attaches the result to EngineResult.
 *
 *           Flags carry a severity so callers can route by tier:
 *             info     — interesting but not a quality concern.
 *             warning  — score is usable but caveat the chip.
 *             blocker  — listing is missing data the engine needs; treat
 *                        the score as low-confidence regardless of math.
 *
 *           Adding a flag is one entry here + the catalog. Disabling a
 *           flag without code: add to DISABLED_FLAGS in the engine config.
 */
import type { ScoringInput } from './types';

export type AnomalyFlag =
  | 'comp_set_thin'
  | 'comp_set_stale'
  | 'hpi_missing'
  | 'price_history_cold'
  | 'incomplete_listing'
  | 'carrying_cost_partial'
  | 'price_anchoring'
  | 'stale_listing'
  | 'outlier_pricing';

export type AnomalySeverity = 'info' | 'warning' | 'blocker';

export const ANOMALY_CATALOG: Record<AnomalyFlag, { severity: AnomalySeverity; label: string }> = {
  comp_set_thin: { severity: 'warning', label: 'Thin comp set' },
  comp_set_stale: { severity: 'warning', label: 'Stale comps' },
  hpi_missing: { severity: 'info', label: 'No HPI coverage' },
  price_history_cold: { severity: 'info', label: 'Price history accruing' },
  incomplete_listing: { severity: 'blocker', label: 'Listing details incomplete' },
  carrying_cost_partial: { severity: 'info', label: 'Partial carrying cost' },
  price_anchoring: { severity: 'info', label: 'Round-number price anchor' },
  stale_listing: { severity: 'warning', label: 'Stale listing' },
  outlier_pricing: { severity: 'warning', label: 'Outlier price for area' },
};

const MIN_COMPS_TIGHT = 3;
const MIN_SNAPSHOTS = 2;
const ANCHOR_THRESHOLDS = [1_000, 5_000, 10_000, 25_000, 50_000, 100_000];
const ANCHOR_PCT = 0.005; // within 0.5% of a round boundary
const STALE_DAYS = 21;
const OUTLIER_PPSF_MULTIPLIER = 2;

/** Reads the engine config to know which flags are silenced. */
function isDisabled(flag: AnomalyFlag, disabledFlags?: ReadonlySet<AnomalyFlag>): boolean {
  return disabledFlags?.has(flag) ?? false;
}

export function detectAnomalies(
  input: ScoringInput,
  disabledFlags?: ReadonlySet<AnomalyFlag>,
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];
  const push = (f: AnomalyFlag) => { if (!isDisabled(f, disabledFlags)) flags.push(f); };

  // ── data-quality flags (E2 originals) ──────────────────────────────────────
  if (input.comparables.comps.length < MIN_COMPS_TIGHT) push('comp_set_thin');
  if (input.comparables.stalenessWarning) push('comp_set_stale');
  if (input.hpi == null || input.hpi.benchmarkPrice == null) push('hpi_missing');
  if (input.priceHistory.snapshots.length < MIN_SNAPSHOTS) push('price_history_cold');
  if (input.listing.livingArea == null || input.listing.yearBuilt == null) push('incomplete_listing');
  const hasStrata = input.listing.associationFee != null;
  const hasTax = input.listing.taxAnnualAmount != null;
  if (hasStrata !== hasTax) push('carrying_cost_partial');

  // ── new signal flags (enterprise sprint) ───────────────────────────────────

  // price_anchoring — strong psychological pricing anchor (e.g. $999K, $1.099M).
  // Tests: is the listing price within 0.5% of any rounded boundary just below
  // a "nice" round number (boundary - $1).
  const lp = input.listing.listPrice;
  if (Number.isFinite(lp) && lp > 0) {
    for (const boundary of ANCHOR_THRESHOLDS) {
      const nearestAbove = Math.ceil(lp / boundary) * boundary;
      // Only fire when listing sits strictly BELOW a round boundary,
      // within ANCHOR_PCT — i.e. the "$999K" / "$1.099M" pattern.
      const gap = nearestAbove - lp;
      if (gap > 0 && gap / lp <= ANCHOR_PCT) { push('price_anchoring'); break; }
    }
  }

  // stale_listing — long time without a price change. We compute the duration
  // for the detection but the UI label is duration-free per BCRES (no
  // days-on-market exposure). Threshold: ≥21d between earliest snapshot and
  // latest, with NO price change across the window.
  const snaps = input.priceHistory.snapshots;
  if (snaps.length >= 2) {
    const first = new Date(snaps[0].snapshotDate).getTime();
    const last = new Date(snaps[snaps.length - 1].snapshotDate).getTime();
    const spanDays = (last - first) / 86_400_000;
    if (spanDays >= STALE_DAYS) {
      const priceChanged = snaps.some((s) => s.listPrice !== snaps[0].listPrice);
      if (!priceChanged) push('stale_listing');
    }
  }

  // outlier_pricing — listing $/sqft > 2× area median ppsf (from the
  // comparables result). Strong signal for either luxury/penthouse OR
  // mispricing; either way warrants the chip.
  const listingPpsf = input.listing.ppsf;
  const medianPpsf = input.comparables.medianPpsf;
  if (listingPpsf != null && medianPpsf != null && medianPpsf > 0) {
    if (listingPpsf > medianPpsf * OUTLIER_PPSF_MULTIPLIER) push('outlier_pricing');
  }

  return flags;
}

/** Sort flags so blockers float to the top in UI lists. */
export function sortBySeverity(flags: AnomalyFlag[]): AnomalyFlag[] {
  const order: Record<AnomalySeverity, number> = { blocker: 0, warning: 1, info: 2 };
  return [...flags].sort((a, b) => order[ANOMALY_CATALOG[a].severity] - order[ANOMALY_CATALOG[b].severity]);
}
